import React, {useEffect, useState} from "react";
import {Box, Button, CircularProgress, Typography} from "@mui/material";

const requestErrorMessage = (error) => {
    const status = error?.response?.status;
    if (status === 403) return "Verify your email address to view and accept workspace invitations.";
    if (status === 404) return "This invitation is no longer available.";
    if (status === 409) return "This workspace is full, or the invitation has already been used.";
    if (status === 410) return "This invitation has expired.";
    return "Something went wrong. Please try again.";
};

const expiryText = (expiresAt) => {
    const seconds = Number(expiresAt);
    if (!Number.isFinite(seconds) || seconds <= 0) return "";
    return new Date(seconds * 1000).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

const primaryActionButtonStyle = {
    minWidth: "88px",
    height: "35px",
    borderRadius: "10px",
    textTransform: "none",
    fontFamily: "Inter",
    fontWeight: 600,
    color: "var(--app-bg-surface)",
    backgroundColor: "var(--app-text-primary)",
    "&:hover": {
        backgroundColor: "var(--app-text-primary)",
        opacity: 0.9,
    },
};

const WorkspaceInvitesSettings = ({authContext, fetchContext}) => {
    const getFreshToken = authContext.getFreshToken;
    const authAxios = fetchContext.authAxios;
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [acceptingWorkspaceId, setAcceptingWorkspaceId] = useState("");
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    useEffect(() => {
        let active = true;
        (async () => {
            setLoading(true);
            setError("");
            try {
                const token = await getFreshToken?.();
                if (!token) throw new Error("Missing authentication token");
                const {data} = await authAxios.get("/workspace-invites", {
                    headers: {Authorization: `Bearer ${token}`},
                });
                if (active) setInvites(Array.isArray(data?.invites) ? data.invites : []);
            } catch (loadError) {
                if (active) setError(requestErrorMessage(loadError));
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [getFreshToken, authAxios]);

    const acceptInvite = async (invite) => {
        const workspaceId = String(invite?.workspaceId || "").trim();
        if (!workspaceId || acceptingWorkspaceId) return;

        setAcceptingWorkspaceId(workspaceId);
        setError("");
        setNotice("");
        try {
            const token = await getFreshToken?.();
            if (!token) throw new Error("Missing authentication token");
            const {data} = await authAxios.post(
                `/workspaces/${encodeURIComponent(workspaceId)}/invites/accept`,
                {},
                {headers: {Authorization: `Bearer ${token}`}}
            );
            authContext.upsertWorkspaceInList?.({
                workspaceId: String(data?.workspaceId || workspaceId).trim(),
                name: String(data?.workspaceName || invite.workspaceName || "").trim(),
                role: String(data?.role || invite.role || "editor").trim(),
            });
            setInvites((current) => current.filter((item) => item.workspaceId !== workspaceId));
            setNotice(`You joined ${data?.workspaceName || invite.workspaceName || "the workspace"}.`);
        } catch (acceptError) {
            setError(requestErrorMessage(acceptError));
        } finally {
            setAcceptingWorkspaceId("");
        }
    };

    return (
        <>
            <Box>
                <Typography sx={{fontFamily: "Inter", fontSize: "15px", fontWeight: 600, color: "var(--app-text-primary)"}}>
                    Workspace invitations
                </Typography>
                <Typography sx={{fontFamily: "Inter", fontSize: "13px", color: "var(--app-text-muted)", mt: "3px"}}>
                    Accept an invitation to add its workspace to your workspace switcher.
                </Typography>
            </Box>

            {error ? (
                <Typography sx={{fontFamily: "Inter", fontSize: "12px", color: "#C63636"}}>{error}</Typography>
            ) : null}
            {notice ? (
                <Typography sx={{fontFamily: "Inter", fontSize: "12px", color: "#2E7D32"}}>{notice}</Typography>
            ) : null}

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", padding: "28px"}}>
                    <CircularProgress size={20}/>
                </Box>
            ) : invites.length === 0 ? (
                <Typography sx={{fontFamily: "Inter", fontSize: "13px", color: "var(--app-text-muted)"}}>
                    You have no pending workspace invitations.
                </Typography>
            ) : (
                <Box sx={{borderTop: "1px solid var(--app-border-subtle)"}}>
                    {invites.map((invite) => {
                        const workspaceId = String(invite.workspaceId || "");
                        const expires = expiryText(invite.expiresAt);
                        return (
                            <Box key={workspaceId} sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "16px",
                                padding: "14px 0",
                                borderBottom: "1px solid var(--app-border-subtle)",
                            }}>
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <Typography noWrap sx={{
                                        fontFamily: "Inter",
                                        fontSize: "14px",
                                        fontWeight: 600,
                                        color: "var(--app-text-primary)",
                                    }}>
                                        {invite.workspaceName || "Workspace"}
                                    </Typography>
                                    <Typography sx={{fontFamily: "Inter", fontSize: "12px", color: "var(--app-text-muted)"}}>
                                        {invite.invitedByEmail
                                            ? `${invite.invitedByEmail} invited you · Editor`
                                            : "Editor"}
                                        {expires ? ` · Expires ${expires}` : ""}
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    disableElevation
                                    disabled={Boolean(acceptingWorkspaceId)}
                                    onClick={() => acceptInvite(invite)}
                                    sx={primaryActionButtonStyle}
                                >
                                    {acceptingWorkspaceId === workspaceId ? "Accepting…" : "Accept"}
                                </Button>
                            </Box>
                        );
                    })}
                </Box>
            )}
        </>
    );
};

export default WorkspaceInvitesSettings;
